import { BatchUpdateRouteParams } from 'Components/App/types';
import { Button, CheckboxTree, Dialog, Field, IMessageProps } from 'Components/Core';
import { CheckboxTreeProps } from 'Components/Core/CheckboxTree/types';
import { IDialogProps } from 'Components/Core/Dialog/types';
import { ChangeEventWithData } from 'Components/Core/types';
import { ListFieldSection, SectionGroup } from 'Components/Layout';
import { ListDropdown } from 'Components/List';
import {
  WorksheetBatchUpdateAllTestsTypeId,
  WorksheetBatchUpdateWorksheetField,
  getWorksheetBatchUpdateDefinition,
} from 'Components/WorksheetBatchUpdate';
import { WorksheetLabelByTypeInjectedPropsProvider } from 'Components/Worksheets/Core';
import { actions } from 'Data/ReduxViewModels/WorksheetBatchUpdate';
import { ListItemModel } from 'Data/ViewModels/ListItemModel';
import { hasEmptyValue, hasNonEmptyValue, hasValue } from 'Lib/Any';
import { useUrlSearchParams } from 'Lib/Hooks';
import deepEquals from 'Lib/Object/deepEquals';
import { pickBy } from 'ramda';
import { useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

/**
 * Dialog that lets the user configure the batch update page
 * by identifying the test method to update and selecting the fields to update
 */
const WorksheetBatchUpdateConfigurationDialog: React.FC<IDialogProps> = ({ onClose, visible }) => {
  // Take type id from route param if provided
  const { typeId: typeIdParam } = useParams<BatchUpdateRouteParams>();
  const uriParams = useUrlSearchParams();
  const uriWorkOrderUuid = uriParams.get('WorkOrder');

  const defaultWorkOrderSelection = useSelector(
    (s) =>
      s.worksheetBatchUpdate.query.enteredParams?.['WorkOrder'] ??
      (hasValue(uriWorkOrderUuid) ? { value: uriWorkOrderUuid!, label: uriWorkOrderUuid! } : null),
    deepEquals
  );

  const [workOrderSelection, setWorkOrderSelection] = useState<ListItemModel | null>(defaultWorkOrderSelection);

  // When the work order selected on the batch update page or provided via the uri changes, we should update the selection here too.
  useEffect(() => {
    if (hasValue(defaultWorkOrderSelection)) setWorkOrderSelection(defaultWorkOrderSelection);
  }, [setWorkOrderSelection, defaultWorkOrderSelection]);

  // Take previously applied configuration from state
  const previousTypeId = useSelector((state) => state.worksheetBatchUpdate.typeId);
  const previousEntryFields = useSelector(
    (state) => Object.keys(pickBy((v, k) => v.update, state.worksheetBatchUpdate.fields)),
    shallowEqual
  );
  const previousWorksheetCaption = useSelector((state) => state.worksheetBatchUpdate.worksheetCaption);

  const dispatch = useDispatch();
  const [typeId, setTypeId] = useState<string | undefined>(previousTypeId ?? typeIdParam);
  const [worksheetCaption, setWorksheetCaption] = useState<string | undefined>(previousWorksheetCaption);
  const [selectedEntryFields, setSelectedEntryFields] = useState(previousEntryFields);

  const definition = typeId ? getWorksheetBatchUpdateDefinition(typeId) : undefined;

  const worksheetType = definition?.worksheetType;
  const worksheetFields = definition?.worksheetFields ?? {};
  const worksheetFieldSections = definition?.worksheetFieldSections ?? {};

  const entryFields: { [name: string]: WorksheetBatchUpdateWorksheetField } = pickBy(
    (value: WorksheetBatchUpdateWorksheetField, _) => hasNonEmptyValue(value.entryComponent),
    worksheetFields
  );

  // Indicates if entry fields are available. Note sections must be defined as well.
  const entryFieldsAvailable = hasNonEmptyValue(entryFields) && hasNonEmptyValue(worksheetFieldSections);

  const testMethodMessages: Array<IMessageProps> = [
    ...(!entryFieldsAvailable && hasNonEmptyValue(typeId)
      ? [
          {
            severity: 'info',
            message: 'Individual fields cannot be updated for the selected method. Worksheets can only be checked.',
          } as IMessageProps,
        ]
      : []),
  ];

  const fieldSelectionMessages: Array<IMessageProps> = [
    ...(entryFieldsAvailable
      ? [
          {
            severity: 'info',
            message: 'Worksheets can be checked without selecting any fields to update.',
          } as IMessageProps,
        ]
      : []),
  ];

  const fieldOptions: CheckboxTreeProps['options'] = Object.entries(worksheetFieldSections).map(
    ([sectionKey, section]) => ({
      label: section.heading,
      name: sectionKey,
      options: Object.entries(entryFields)
        .filter(([_, v]) => v.section === sectionKey)
        .map(([fieldKey, _]) => ({
          name: fieldKey,
        })),
    })
  );

  return (
    <Dialog
      title="Configure Batch Update Page"
      width={500}
      visible={visible}
      buttons={[
        <Button
          primary
          key="1"
          onClick={() => {
            dispatch(
              actions.configure({
                typeId: typeId!,
                worksheetCaption: worksheetCaption!,
                worksheetType: worksheetType!,
                updateFields: selectedEntryFields,
                selectedWorkOrder: workOrderSelection ?? undefined,
              })
            );
            onClose?.();
          }}
          disabled={hasEmptyValue(typeId) || hasEmptyValue(workOrderSelection)}
        >
          Done
        </Button>,
        <Button
          key="0"
          onClick={() => {
            setWorksheetCaption(previousWorksheetCaption);
            setTypeId(previousTypeId ?? typeIdParam);
            setSelectedEntryFields(previousEntryFields);
            onClose?.();
          }}
        >
          Cancel
        </Button>,
      ]}
    >
      <SectionGroup>
        <ListFieldSection wide messages={testMethodMessages}>
          {
            // Hide work order selection when within a frame (QESTLab)
            // This is to mask an issue where we cannot query for an appropriate label for the work order (which is always injected via uri)
            // See QP-4701
            !window.qest.isFrame && (
              <Field
                input={<ListDropdown />}
                horizontal
                label="Work Order"
                endpoint="list.workorders"
                queryParams={{ orderBy: 'WorkDate desc' }}
                searchImmediately={true}
                isClearable={false}
                maxResults={20}
                value={workOrderSelection}
                placeholder="Select or enter Work Order ID to search"
                onChange={(e: ChangeEventWithData) => setWorkOrderSelection(e?.data?.value)}
              />
            )
          }
          <Field
            input={<ListDropdown />}
            horizontal
            endpoint="list.worksheetTypes"
            label="Test Method"
            autoSelectedValue={WorksheetBatchUpdateAllTestsTypeId}
            additionalOptions={[{ value: WorksheetBatchUpdateAllTestsTypeId, label: 'All Tests' }]}
            value={typeId ?? null}
            queryParams={{ IncludeNonWebAppWorksheets: true, WorkOrderId: workOrderSelection?.value }}
            // Take caption from loaded list for current value
            onListChange={(e) => {
              if (hasValue(typeId)) {
                const listItem = e.list.find((l: any) => l.value === typeId);
                if (hasValue(listItem)) setWorksheetCaption(e.list.find((l: any) => l.value === typeId)?.label);
                else {
                  setTypeId(undefined);
                  setWorksheetCaption(undefined);
                  setSelectedEntryFields([]);
                }
              }
            }}
            // When test method changes, take caption from list item and reset entry field selection
            onChange={(e: any) => {
              if (e.data?.value?.value !== typeId) {
                setTypeId(e.data?.value?.value);
                setWorksheetCaption(e.data?.value?.label);
                setSelectedEntryFields([]);
              }
            }}
            searchImmediately
            // Disable selection if test method was passed in via uri parameter by a wrapping frame (i.e. QESTLab)
            disabled={hasEmptyValue(workOrderSelection) || (hasValue(typeIdParam) && window.qest.isFrame)}
          />
        </ListFieldSection>
        {hasNonEmptyValue(worksheetFieldSections) && (
          <ListFieldSection wide heading="Select Fields to Update" messages={fieldSelectionMessages}>
            <WorksheetLabelByTypeInjectedPropsProvider typeId={typeId!}>
              <CheckboxTree
                options={fieldOptions}
                selectedOptions={selectedEntryFields}
                onSelectionChange={(s) => setSelectedEntryFields(s)}
              />
            </WorksheetLabelByTypeInjectedPropsProvider>
          </ListFieldSection>
        )}
      </SectionGroup>
    </Dialog>
  );
};

export { WorksheetBatchUpdateConfigurationDialog };
export default WorksheetBatchUpdateConfigurationDialog;
